import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell,
} from "recharts";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

const MONTH_LABELS = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
];

function MonthlyPerformance({ filterContext, bundleData, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const processRows = useCallback((rows) => {
        const monthMap = {};
        rows.forEach((r) => {
            const m = r.month || 1;
            monthMap[m] = {
                value_aed: Number(r.value_aed || 0),
                deal_count: Number(r.deal_count || 0)
            };
        });
        return MONTH_LABELS.map((name, idx) => ({
            month: name,
            monthIndex: idx + 1,
            value_aed: monthMap[idx + 1]?.value_aed || 0,
            deal_count: monthMap[idx + 1]?.deal_count || 0,
        }));
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(`${API_BASE}/api/metrics/monthly-performance`, {
                params: { salesperson, year, region: region || "all" },
            });
            const rows = res.data.data || [];
            setData(processRows(rows));
        } catch (err) {
            console.error("MonthlyPerformance error", err);
            setError(err.message || "Failed to load monthly performance");
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region, processRows]);

    useEffect(() => {
        if (bundleData) {
            try {
                const rows = bundleData.data || [];
                setData(processRows(rows));
                setLoading(false);
                setError("");
            } catch (err) {
                console.error("Error processing bundle data:", err);
                setError("Failed to process monthly performance data");
            }
            return;
        }
        if (!salesperson || !year) return;
        fetchData();
    }, [salesperson, year, region, bundleData, processRows, fetchData]);

    const hasData = useMemo(() => data.some((d) => d.value_aed > 0), [data]);

    const totals = useMemo(() => ({
        totalValue: data.reduce((sum, d) => sum + d.value_aed, 0),
        avgValue: data.reduce((sum, d) => sum + d.value_aed, 0) / 12
    }), [data]);

    const CustomTooltip = useCallback(({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: "rgba(15, 23, 42, 0.95)",
                    backdropFilter: "blur(12px)",
                    border: "2px solid #ff6b35",
                    borderRadius: 12,
                    padding: "16px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 15px rgba(255,107,53,0.1)"
                }}>
                    <p style={{ margin: "0 0 8px 0", color: "#ffffff", fontSize: 16, fontWeight: 800, textTransform: "uppercase" }}>{label}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <p style={{ margin: 0, color: "#ff6b35", fontSize: 14, fontWeight: 700 }}>
                            Sales: <span style={{ color: "#fff", fontSize: 18 }}>{displayCurrency} {Math.round(Number(payload[0].value)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </p>
                        <p style={{ margin: 0, color: "#ff6b35", fontSize: 14, fontWeight: 700 }}>
                            Deals: <span style={{ color: "#fff", fontSize: 18 }}>{payload[0].payload.deal_count}</span>
                        </p>
                    </div>
                </div>
            );
        }
        return null;
    }, [displayCurrency]);

    const actionButtons = useMemo(() => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {onMaximize && !fullScreen && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        color: "#e5e7eb",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: 8,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                    }}
                    title="Maximize"
                >
                    <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
                </button>
            )}
            {fullScreen && onMaximize && (
                <button
                    onClick={onMaximize}
                    style={{
                        padding: "8px",
                        background: "rgba(255, 107, 53, 0.2)",
                        color: "#ff6b35",
                        border: "1px solid #ff6b35",
                        borderRadius: "50%",
                        width: "36px",
                        height: "36px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                    }}
                >

                </button>
            )}
        </div>
    ), [onMaximize, fullScreen]);

    const Subtitle = () => (
        <p style={{
            color: "#f8fafc",
            fontSize: fullScreen ? "24px" : "18px",
            marginTop: fullScreen ? "0px" : "-10px",
            marginBottom: theme.spacing.lg,
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textAlign: fullScreen ? "center" : "left",
            textShadow: "0 2px 4px rgba(0,0,0,0.5)"
        }}>
            {salesperson && <span style={{ color: "#ff8a3d" }}>{salesperson}</span>}
            {year && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {year}</span>}
            {region && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {region === "all" ? "All Regions" : region}</span>}
        </p>
    );

    const ChartArea = () => (
        <div style={{
            flex: fullScreen ? "1 1 auto" : "none",
            width: "100%",
            height: fullScreen ? "auto" : "350px",
            minHeight: 0,
            marginTop: fullScreen ? "20px" : "20px"
        }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart
                    data={data}
                    margin={{ top: fullScreen ? 20 : 10, right: fullScreen ? 50 : 30, left: fullScreen ? 20 : 0, bottom: fullScreen ? 80 : 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                    <XAxis
                        dataKey="month"
                        stroke="#ffffff"
                        tick={{ fill: "#ffffff", fontSize: fullScreen ? 14 : 11, fontWeight: 700, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif" }}
                        angle={-30}
                        textAnchor="end"
                        height={fullScreen ? 100 : 75}
                        interval={0}
                        tickFormatter={(v) => v.substring(0, 3)}
                        axisLine={{ stroke: "rgba(255,255,255,0.4)", strokeWidth: 2 }}
                        label={{
                            value: 'MONTHS',
                            position: 'insideBottom',
                            offset: -5,
                            fill: '#94a3b8',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 1
                        }}
                    />
                    <YAxis
                        stroke="#ffffff"
                        tick={{ fill: "#ffffff", fontSize: fullScreen ? 14 : 11, fontWeight: 700, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif" }}
                        tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}M`}
                        width={fullScreen ? 80 : 60}
                        axisLine={{ stroke: "rgba(255,255,255,0.4)", strokeWidth: 2 }}
                        label={{
                            value: `VALUE (${displayCurrency})`,
                            angle: -90,
                            position: 'insideLeft',
                            offset: 10,
                            fill: '#94a3b8',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 1,
                            style: { textAnchor: 'middle' }
                        }}
                    />
                    <Tooltip
                        content={<CustomTooltip />}
                        cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                    />
                    <defs>
                        <linearGradient id="monthlyGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ff6b35" stopOpacity={1} />
                            <stop offset="100%" stopColor="#ff9f7d" stopOpacity={0.8} />
                        </linearGradient>
                    </defs>
                    <Bar
                        dataKey="value_aed"
                        name={`Closed Won (${displayCurrency})`}
                        radius={fullScreen ? [8, 8, 4, 4] : [4, 4, 2, 2]}
                        animationDuration={1000}
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill="url(#monthlyGradient)" style={{ transition: 'all 0.3s ease' }} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    const SummaryItems = () => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "18px", marginBottom: "18px", position: "relative", zIndex: 1, justifyContent: fullScreen ? "center" : "flex-start" }}>
            {/* Total Closed Won Value */}
            <div style={{
                flex: "1 1 auto",
                minWidth: "220px",
                maxWidth: fullScreen ? "400px" : "none",
                background: "linear-gradient(135deg, rgba(255,107,53,0.1) 0%, rgba(255,107,53,0.02) 100%)",
                borderRadius: 12,
                padding: fullScreen ? "18px 24px" : "14px 20px",
                border: "1px solid rgba(255,107,53,0.2)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}>
                <div style={{ color: "#ff6b35", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>
                    Total Closed Won ({year})
                </div>
                <div style={{ color: "#ffffff", fontSize: fullScreen ? 28 : 24, fontWeight: 800 }}>
                    {displayCurrency} <span className="text-gradient-orange">{Math.round(totals.totalValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
            </div>
            {/* Average per month */}
            <div style={{
                flex: "1 1 auto",
                minWidth: "220px",
                maxWidth: fullScreen ? "400px" : "none",
                background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.02) 100%)",
                borderRadius: 12,
                padding: fullScreen ? "18px 24px" : "14px 20px",
                border: "1px solid rgba(59, 130, 246, 0.2)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}>
                <div style={{ color: "#60a5fa", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>
                    Avg Closed Won / Month
                </div>
                <div style={{ color: "#ffffff", fontSize: fullScreen ? 28 : 24, fontWeight: 800 }}>
                    {displayCurrency} <span className="text-gradient-blue">{Math.round(totals.avgValue).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
            </div>
        </div>
    );

    if (!salesperson || !year) return null;

    return (
        <DashboardCard
            title="Monthly Performance"
            actions={actionButtons}
            fullScreen={fullScreen}
            gradient="primary"
        >
            <Subtitle />
            {loading && <SkeletonCard height="400px" />}
            {error && (
                <div style={{ padding: theme.spacing.lg, background: "rgba(239, 68, 68, 0.1)", borderRadius: theme.borderRadius.md, border: `1px solid ${theme.colors.danger}`, color: "#fca5a5" }}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            {!loading && !error && !hasData && (
                <EmptyState icon="" message="No data found for this period." size="medium" />
            )}
            {!loading && !error && hasData && (
                <div style={{
                    height: fullScreen ? "100%" : "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: fullScreen ? "center" : "flex-start",
                    overflowY: fullScreen ? "hidden" : "visible",
                    paddingBottom: fullScreen ? "20px" : "0"
                }}>
                    <SummaryItems />
                    <ChartArea />
                </div>
            )}
        </DashboardCard>
    );
}

export default MonthlyPerformance;
